import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BulkCandidate } from '@/types/portal';

interface RecipientReadinessProps {
  /** Creators that can be added to the batch now. */
  ready: number;
  /** Hired creators who aren't payable yet, each with `waiting` saying why. */
  waiting: BulkCandidate[];
}

const NAMES_SHOWN = 4;

/**
 * Above the recipient tabs: how many creators are ready to pay and why the rest
 * aren't, so an empty search or campaign list explains itself.
 */
export default function RecipientReadiness({ ready, waiting }: RecipientReadinessProps) {
  const [open, setOpen] = useState(false);

  const groups = useMemo(() => {
    const map = new Map<string, BulkCandidate[]>();
    waiting.forEach((candidate) => {
      // Group on the reason without its per-creator count, e.g. "(2 of 3 approved)".
      const reason = (candidate.waiting ?? '').replace(/\s*\(.*\)$/, '');
      map.set(reason, [...(map.get(reason) ?? []), candidate]);
    });
    return Array.from(map.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [waiting]);

  if (ready === 0 && waiting.length === 0) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-3 text-sm text-foreground-700">
        <i className="ri-information-line mt-0.5 text-foreground-500" />
        <p>
          No creators are ready to pay. A creator becomes payable when all their content is
          approved.{' '}
          <Link to="/campaigns" className="font-medium text-primary-700 underline-offset-2 hover:underline">
            Open campaigns
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-background-200 bg-background-100 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="inline-flex items-center gap-1.5 text-foreground-800">
          <span className="h-2 w-2 rounded-full bg-accent-500" aria-hidden="true" />
          <strong className="text-foreground-950">{ready}</strong> ready to pay
        </span>
        {waiting.length > 0 ? (
          <>
            <span className="inline-flex items-center gap-1.5 text-foreground-800">
              <span className="h-2 w-2 rounded-full bg-secondary-500" aria-hidden="true" />
              <strong className="text-foreground-950">{waiting.length}</strong> not payable yet
            </span>
            <button
              type="button"
              onClick={() => setOpen((value) => !value)}
              aria-expanded={open}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 cursor-pointer underline-offset-2 hover:underline"
            >
              {open ? 'Hide why' : 'Show why'}
              <i className={open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
            </button>
          </>
        ) : null}
      </div>

      {ready === 0 ? (
        <p className="mt-1.5 text-xs text-foreground-600">
          Nobody can be paid yet. A creator becomes payable when all their content is approved.
        </p>
      ) : null}

      {open ? (
        <ul className="mt-3 space-y-2 border-t border-background-200 pt-3 animate-fade-in">
          {groups.map(([reason, people]) => (
            <li key={reason} className="text-xs">
              <span className="font-medium text-foreground-800">
                {reason} · {people.length}
              </span>
              <span className="block text-foreground-500">
                {people
                  .slice(0, NAMES_SHOWN)
                  .map((person) => `${person.name || 'Unnamed creator'} (${person.campaign})`)
                  .join(', ')}
                {people.length > NAMES_SHOWN ? ` and ${people.length - NAMES_SHOWN} more` : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
